import React, { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  HardDrive, Plus, Search, ChevronDown, ChevronUp, Trash2, UserCheck, UserMinus,
  Package, Truck, CheckCircle2, RefreshCw, X
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  received: { label: 'Received', color: 'bg-blue-500/15 text-blue-300 border-blue-500/30', icon: Package },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

// Wrap matching parts of text in a yellow highlight <mark> for search results
const Highlight = ({ text, term }) => {
  if (!term || !text) return <>{text}</>;
  const safeTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${safeTerm})`, 'gi'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === term.toLowerCase()
          ? <mark key={i} className="bg-[#F5A623]/40 text-[#F5A623] rounded px-0.5">{p}</mark>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </>
  );
};

const HardDrivesTab = ({ isManager }) => {
  const [subTab, setSubTab] = useState('all');
  const [drives, setDrives] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ label: '', capacity_gb: '', notes: '', location: '', client_name: '', work_description: '' });
  const [showAddClient, setShowAddClient] = useState(null); // drive_id
  const [clientForm, setClientForm] = useState({ client_name: '', project_title: '', notes: '' });
  const [showCheckout, setShowCheckout] = useState(null); // drive
  const [checkoutForm, setCheckoutForm] = useState({ user_id: '', notes: '' });

  const fetchDrives = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/drives', { params: { kind: subTab, ...(search ? { search } : {}) } });
      setDrives(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [subTab, search]);

  useEffect(() => { fetchDrives(); }, [fetchDrives]);

  useEffect(() => {
    api.get('/users').then(r => setTeamMembers(r.data || [])).catch(() => {});
  }, []);

  const resetCreate = () => setCreateForm({ label: '', capacity_gb: '', notes: '', location: '', client_name: '', work_description: '' });

  const createKind = subTab === 'all' ? 'studio' : subTab;

  const handleCreate = async () => {
    if (!createForm.label.trim()) return toast.error('Drive label is required');
    if (createKind === 'client' && !createForm.client_name.trim()) return toast.error('Client name is required for client drives');
    try {
      await api.post('/drives', {
        label: createForm.label.trim(),
        kind: createKind,
        capacity_gb: createForm.capacity_gb ? Number(createForm.capacity_gb) : null,
        notes: createForm.notes || null,
        location: createForm.location || null,
        ...(createKind === 'client' ? {
          client_name: createForm.client_name.trim(),
          work_description: createForm.work_description || null,
        } : {}),
      });
      toast.success('Drive added');
      setShowCreate(false);
      resetCreate();
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this drive entry? The drive itself is not affected, only the record.')) return;
    try {
      await api.delete(`/drives/${id}`);
      toast.success('Removed');
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleAddClient = async () => {
    if (!clientForm.client_name.trim()) return toast.error('Client name is required');
    try {
      await api.post(`/drives/${showAddClient}/clients`, {
        client_name: clientForm.client_name.trim(),
        project_title: clientForm.project_title || null,
        notes: clientForm.notes || null,
      });
      toast.success('Client added');
      setShowAddClient(null);
      setClientForm({ client_name: '', project_title: '', notes: '' });
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleRemoveClient = async (driveId, entryId) => {
    if (!window.confirm('Remove this client entry from the drive?')) return;
    try {
      await api.delete(`/drives/${driveId}/clients/${entryId}`);
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleCheckout = async () => {
    if (!checkoutForm.user_id) return toast.error('Select a team member');
    try {
      await api.post(`/drives/${showCheckout.id}/checkout`, checkoutForm);
      toast.success('Drive checked out');
      setShowCheckout(null);
      setCheckoutForm({ user_id: '', notes: '' });
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleCheckin = async (driveId) => {
    if (!window.confirm('Mark this drive as returned to the studio?')) return;
    try {
      await api.post(`/drives/${driveId}/checkin`);
      toast.success('Drive returned');
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleStatusChange = async (driveId, status) => {
    let notes = null;
    if (status === 'delivered') {
      notes = window.prompt('Delivery notes (optional):') || null;
    }
    try {
      await api.post(`/drives/${driveId}/status`, { status, delivery_notes: notes });
      toast.success(`Status: ${STATUS_META[status].label}`);
      fetchDrives();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="hard-drives-tab">
      {/* Sub-tabs + search + add */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-white/5 rounded-md p-1 gap-1">
          <button
            onClick={() => { setSubTab('all'); setExpandedId(null); }}
            data-testid="drive-subtab-all"
            className={`px-4 py-1.5 text-sm rounded ${subTab === 'all' ? 'bg-[#F5A623] text-black font-medium' : 'text-zinc-400 hover:text-white'}`}>
            All Drives
          </button>
          <button
            onClick={() => { setSubTab('studio'); setExpandedId(null); }}
            data-testid="drive-subtab-studio"
            className={`px-4 py-1.5 text-sm rounded ${subTab === 'studio' ? 'bg-[#F5A623] text-black font-medium' : 'text-zinc-400 hover:text-white'}`}>
            Studio Drives
          </button>
          <button
            onClick={() => { setSubTab('backup'); setExpandedId(null); }}
            data-testid="drive-subtab-backup"
            className={`px-4 py-1.5 text-sm rounded ${subTab === 'backup' ? 'bg-[#F5A623] text-black font-medium' : 'text-zinc-400 hover:text-white'}`}>
            Backup Drives
          </button>
          <button
            onClick={() => { setSubTab('client'); setExpandedId(null); }}
            data-testid="drive-subtab-client"
            className={`px-4 py-1.5 text-sm rounded ${subTab === 'client' ? 'bg-[#F5A623] text-black font-medium' : 'text-zinc-400 hover:text-white'}`}>
            Client Drives
          </button>
        </div>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              subTab === 'all' ? 'Search across all drives — label, client, holder, work…' :
              subTab === 'studio' ? 'Search by drive #, client, holder, project…' :
              'Search by drive #, client, work…'
            }
            className="pl-9 bg-white/5 border-white/10"
            data-testid="drives-search"
          />
        </div>

        <Button variant="outline" size="sm" onClick={fetchDrives} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>

        <Button onClick={() => { resetCreate(); setShowCreate(true); }} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="add-drive-btn">
          <Plus className="w-4 h-4 mr-1" /> Add {createKind === 'studio' ? 'Studio Drive' : createKind === 'backup' ? 'Backup Drive' : 'Client Drive'}
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-white/50 py-12 text-center">Loading…</div>
      ) : drives.length === 0 ? (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-10 text-center text-white/50">
            <HardDrive className="w-10 h-10 mx-auto mb-2 text-white/30" />
            {search ? 'No drives match your search.' : `No ${subTab === 'all' ? '' : subTab + ' '}drives yet. Click "Add" to register one.`}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="drives-list">
          {drives.map(d => {
            const expanded = expandedId === d.id;
            const StatusIcon = d.status ? STATUS_META[d.status]?.icon : null;
            // In "all" mode, derive from the drive itself; otherwise use the active tab
            const rowKind = subTab === 'all' ? (d.kind || 'studio') : subTab;
            return (
              <Card key={d.id} className="bg-[#121212] border-white/10">
                <CardContent className="p-0">
                  {/* Row header */}
                  <div className="flex flex-wrap items-center gap-3 p-4">
                    <button
                      onClick={() => setExpandedId(expanded ? null : d.id)}
                      className="flex-1 min-w-[200px] text-left"
                      data-testid={`drive-row-${d.id}`}>
                      <div className="flex items-center gap-2 font-semibold">
                        {expanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                        <HardDrive className="w-4 h-4 text-[#F5A623]" />
                        <Highlight text={d.label} term={search} />
                        {d.capacity_gb && <span className="text-xs text-white/40 font-normal">· {d.capacity_gb} GB</span>}
                        {subTab === 'all' && (
                          <Badge variant="outline" className={`ml-1 text-[10px] capitalize ${
                            rowKind === 'client' ? 'bg-purple-500/10 text-purple-300 border-purple-500/30' :
                            rowKind === 'backup' ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' :
                            'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                          }`}>{rowKind}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-white/50 mt-1 ml-6">
                        {rowKind === 'studio' ? (
                          <>
                            {(d.clients_stored || []).length} client backup{(d.clients_stored || []).length === 1 ? '' : 's'}
                            {d.current_holder && <> · <span className="text-amber-300">with {d.current_holder.user_name}</span></>}
                            {!d.current_holder && <> · <span className="text-emerald-300">in studio</span></>}
                            {d.location && <> · {d.location}</>}
                          </>
                        ) : rowKind === 'backup' ? (
                          <>
                            {(d.clients_stored || []).length} client{(d.clients_stored || []).length === 1 ? '' : 's'} backed up
                            {d.capacity_gb && <> · {d.capacity_gb} GB</>}
                            {d.location && <> · <Highlight text={d.location} term={search} /></>}
                            {d.current_holder && <> · <span className="text-amber-300">with <Highlight text={d.current_holder.user_name} term={search} /></span></>}
                          </>
                        ) : (
                          <>
                            From <strong><Highlight text={d.client_name} term={search} /></strong>
                            {d.received_at && <> · received {formatDate(d.received_at)}</>}
                          </>
                        )}
                      </div>
                    </button>

                    {/* Status / action area */}
                    {rowKind === 'client' && d.status && (
                      <Badge className={`${STATUS_META[d.status].color} border`}>
                        {StatusIcon && <StatusIcon className="w-3 h-3 mr-1" />}
                        {STATUS_META[d.status].label}
                      </Badge>
                    )}

                    {(rowKind === 'studio' || rowKind === 'backup') && (
                      d.current_holder ? (
                        <Button size="sm" variant="outline" onClick={() => handleCheckin(d.id)} data-testid={`checkin-${d.id}`}>
                          <UserMinus className="w-4 h-4 mr-1" /> Return
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => { setCheckoutForm({ user_id: '', notes: '' }); setShowCheckout(d); }} data-testid={`checkout-${d.id}`}>
                          <UserCheck className="w-4 h-4 mr-1" /> Allocate
                        </Button>
                      )
                    )}

                    {rowKind === 'client' && d.status !== 'delivered' && (
                      <Select value="" onValueChange={(v) => handleStatusChange(d.id, v)}>
                        <SelectTrigger className="w-[150px] h-8 bg-white/5 border-white/10" data-testid={`status-select-${d.id}`}>
                          <SelectValue placeholder="Change status" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a1a] border-white/10">
                          {['received', 'in_progress', 'delivered'].filter(s => s !== d.status).map(s => (
                            <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {isManager && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} data-testid={`delete-drive-${d.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </div>

                  {/* Expanded body */}
                  {expanded && (
                    <div className="border-t border-white/5 p-4 space-y-4 text-sm" data-testid={`drive-detail-${d.id}`}>
                      {d.notes && <div className="text-white/70 italic"><Highlight text={d.notes} term={search} /></div>}

                      {(rowKind === 'studio' || rowKind === 'backup') && (
                        <>
                          {/* Clients on this drive */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-white/80">Clients stored on this drive</span>
                              <Button size="sm" variant="outline" onClick={() => { setClientForm({ client_name: '', project_title: '', notes: '' }); setShowAddClient(d.id); }} data-testid={`add-client-${d.id}`}>
                                <Plus className="w-3 h-3 mr-1" /> Add client
                              </Button>
                            </div>
                            {(d.clients_stored || []).length === 0 ? (
                              <div className="text-xs text-white/40">No client backups recorded.</div>
                            ) : (
                              <div className="space-y-1">
                                {(d.clients_stored || []).map(c => (
                                  <div key={c.entry_id} className="flex items-start justify-between gap-2 bg-white/[0.03] rounded px-3 py-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium"><Highlight text={c.client_name} term={search} /></div>
                                      {c.project_title && <div className="text-xs text-white/50">Project: <Highlight text={c.project_title} term={search} /></div>}
                                      {c.notes && <div className="text-xs text-white/60 mt-0.5"><Highlight text={c.notes} term={search} /></div>}
                                      <div className="text-xs text-white/30 mt-1">Added {formatDate(c.added_at)} by {c.added_by_name}</div>
                                    </div>
                                    <button onClick={() => handleRemoveClient(d.id, c.entry_id)} className="text-white/40 hover:text-red-400 shrink-0">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Allocation history */}
                          {(d.allocation_history || []).length > 0 && (
                            <div>
                              <div className="font-medium text-white/80 mb-2">Allocation history</div>
                              <div className="space-y-1">
                                {(d.allocation_history || []).slice().reverse().map((h, i) => (
                                  <div key={i} className="text-xs bg-white/[0.03] rounded px-3 py-2">
                                    <span className="text-white/80 font-medium">{h.user_name}</span>
                                    <span className="text-white/40"> · checked out {formatDate(h.checked_out_at)}</span>
                                    {h.checked_in_at
                                      ? <span className="text-emerald-300"> · returned {formatDate(h.checked_in_at)}</span>
                                      : <span className="text-amber-300"> · still out</span>}
                                    {h.notes && <div className="text-white/50 mt-0.5">{h.notes}</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {rowKind === 'client' && (
                        <>
                          {d.work_description && (
                            <div>
                              <div className="font-medium text-white/80 mb-1">Work to do</div>
                              <div className="text-white/70 whitespace-pre-line"><Highlight text={d.work_description} term={search} /></div>
                            </div>
                          )}
                          {d.delivered_at && (
                            <div className="text-xs text-emerald-300">Delivered back on {formatDate(d.delivered_at)}</div>
                          )}
                          {(d.status_history || []).length > 0 && (
                            <div>
                              <div className="font-medium text-white/80 mb-2">Status history</div>
                              <div className="space-y-1">
                                {(d.status_history || []).slice().reverse().map((h, i) => (
                                  <div key={i} className="text-xs bg-white/[0.03] rounded px-3 py-2">
                                    <span className="text-white/80 font-medium">{STATUS_META[h.status]?.label || h.status}</span>
                                    <span className="text-white/40"> · {formatDate(h.at)} · by {h.by_name}</span>
                                    {h.notes && <div className="text-white/50 mt-0.5">{h.notes}</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <div className="text-xs text-white/30 pt-2 border-t border-white/5">
                        Added {formatDate(d.created_at)} by {d.created_by_name}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add {createKind === 'studio' ? 'Studio Drive' : createKind === 'backup' ? 'Backup Drive' : 'Client Drive'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="add-drive-dialog">
            <div>
              <Label className="text-sm text-white/70">Drive label *</Label>
              <Input value={createForm.label} onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                placeholder="e.g. WS-Backup-2026-01" className="bg-white/5 border-white/10" data-testid="new-drive-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-white/70">Capacity (GB)</Label>
                <Input type="number" value={createForm.capacity_gb} onChange={(e) => setCreateForm({ ...createForm, capacity_gb: e.target.value })}
                  placeholder="e.g. 4000" className="bg-white/5 border-white/10" />
              </div>
              <div>
                <Label className="text-sm text-white/70">Location</Label>
                <Input value={createForm.location} onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  placeholder="e.g. Shelf 3" className="bg-white/5 border-white/10" />
              </div>
            </div>
            {createKind === 'client' && (
              <>
                <div>
                  <Label className="text-sm text-white/70">Client name *</Label>
                  <Input value={createForm.client_name} onChange={(e) => setCreateForm({ ...createForm, client_name: e.target.value })}
                    placeholder="Client / event name" className="bg-white/5 border-white/10" data-testid="new-drive-client" />
                </div>
                <div>
                  <Label className="text-sm text-white/70">Work to do</Label>
                  <Textarea value={createForm.work_description} onChange={(e) => setCreateForm({ ...createForm, work_description: e.target.value })}
                    rows={2} placeholder="What needs to be done with this drive" className="bg-white/5 border-white/10" />
                </div>
              </>
            )}
            <div>
              <Label className="text-sm text-white/70">Notes</Label>
              <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                rows={2} className="bg-white/5 border-white/10" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="submit-new-drive">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add client to drive dialog */}
      <Dialog open={!!showAddClient} onOpenChange={(o) => !o && setShowAddClient(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add client backup entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="add-client-dialog">
            <div>
              <Label className="text-sm text-white/70">Client name *</Label>
              <Input value={clientForm.client_name} onChange={(e) => setClientForm({ ...clientForm, client_name: e.target.value })}
                className="bg-white/5 border-white/10" data-testid="client-entry-name" />
            </div>
            <div>
              <Label className="text-sm text-white/70">Project link / title</Label>
              <Input value={clientForm.project_title} onChange={(e) => setClientForm({ ...clientForm, project_title: e.target.value })}
                placeholder="e.g. Reception highlights 2026" className="bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-sm text-white/70">Notes</Label>
              <Textarea value={clientForm.notes} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                rows={2} placeholder="What's stored here?" className="bg-white/5 border-white/10" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddClient(null)}>Cancel</Button>
              <Button onClick={handleAddClient} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="submit-client-entry">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout dialog */}
      <Dialog open={!!showCheckout} onOpenChange={(o) => !o && setShowCheckout(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate drive {showCheckout?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="checkout-dialog">
            <div>
              <Label className="text-sm text-white/70">Allocate to *</Label>
              <Select value={checkoutForm.user_id} onValueChange={(v) => setCheckoutForm({ ...checkoutForm, user_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10" data-testid="checkout-user-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {teamMembers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name} <span className="text-white/40 text-xs">({m.role})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-white/70">Notes</Label>
              <Textarea value={checkoutForm.notes} onChange={(e) => setCheckoutForm({ ...checkoutForm, notes: e.target.value })}
                rows={2} placeholder="Reason / project this drive is for" className="bg-white/5 border-white/10" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCheckout(null)}>Cancel</Button>
              <Button onClick={handleCheckout} className="bg-[#F5A623] text-black hover:bg-[#F5A623]/90" data-testid="submit-checkout">Allocate</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HardDrivesTab;
